import { useEffect, useState, type FormEvent } from 'react';
import { Check, LoaderCircle, X } from 'lucide-react';
import { ApiError, publicApi, type CompanySignupBody } from '../../lib/apiClient';
import { validateContactEmail, validateContactName } from '../../lib/contactValidation';
import './EmployerForm.css';

interface EmployerFormState {
  name: string;
  email: string;
  company: string;
  role: string;
  location: string;
  timeline: string;
  notes: string;
}

type ValidatedField = 'name' | 'email' | 'company' | 'location';

const INITIAL_FORM: EmployerFormState = {
  name: '',
  email: '',
  company: '',
  role: '',
  location: '',
  timeline: '',
  notes: '',
};

const REGIONS = [
  {
    slug: 'usa',
    name: 'United States',
  },
  {
    slug: 'canada',
    name: 'Canada',
  },
  {
    slug: 'india',
    name: 'India',
  },
  {
    slug: 'thailand',
    name: 'Thailand',
  },
] as const;

function findRegion(regionSlug: string) {
  return REGIONS.find((region) => region.slug === regionSlug);
}

const TIMELINE_LABELS: Record<string, string> = {
  immediately: 'Immediately',
  '30-days': 'Within 30 days',
  '60-days': 'Within 60 days',
  flexible: 'Flexible',
};

function buildCompanyDescription(form: EmployerFormState): string {
  const region = findRegion(form.location);
  return [
    `Role: ${form.role.trim() || 'Not provided'}`,
    `Location: ${region?.name ?? 'Not provided'}`,
    `Timeline: ${TIMELINE_LABELS[form.timeline] ?? 'Not provided'}`,
    `Additional notes: ${form.notes.trim() || 'Not provided'}`,
  ].join('\n');
}

export const EmployerForm = () => {
  const [form, setForm] = useState<EmployerFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<ValidatedField, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!submittedEmail) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSubmittedEmail(null);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [submittedEmail]);

  function updateField<K extends keyof EmployerFormState>(key: K, value: EmployerFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setSubmitError(null);

    if (
      key === 'name' ||
      key === 'email' ||
      key === 'company' ||
      key === 'location'
    ) {
      const errorKey: ValidatedField = key;
      setErrors((current) => {
        if (!current[errorKey]) return current;
        const next = { ...current };
        delete next[errorKey];
        return next;
      });
    }
  }

  function validateField(field: 'name' | 'email', value: string) {
    const error = field === 'name' ? validateContactName(value) : validateContactEmail(value);
    setErrors((current) => {
      const next = { ...current };
      if (error) next[field] = error;
      else delete next[field];
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;

    const nameError = validateContactName(form.name);
    const emailError = validateContactEmail(form.email);
    const companyError = form.company.trim() ? null : 'Company name is required';
    const region = findRegion(form.location);
    const locationError = region ? null : 'Please select a valid location';
    const nextErrors: Partial<Record<ValidatedField, string>> = {};
    if (nameError) nextErrors.name = nameError;
    if (emailError) nextErrors.email = emailError;
    if (companyError) nextErrors.company = companyError;
    if (locationError) nextErrors.location = locationError;
    setErrors(nextErrors);

    if (nameError || emailError || companyError || locationError) {
      const firstInvalid = nameError
        ? 'name'
        : emailError
          ? 'email'
          : companyError
            ? 'company'
            : 'location';
      document.getElementById(`employer-${firstInvalid}`)?.focus();
      return;
    }

    const description = buildCompanyDescription(form);
    if (description.length > 2000) {
      setSubmitError('Your request details are too long. Please shorten them and try again.');
      document.getElementById('employer-notes')?.focus();
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const email = form.email.trim().toLowerCase();
      const payload: CompanySignupBody = {
        company: {
          name: form.company.trim(),
          region_slugs: [region!.slug],
          description,
        },
        admin: {
          full_name: form.name.trim(),
          email,
        },
        consent: {
          accepted_privacy_policy: true,
          accepted_terms: true,
        },
      };
      await publicApi.submitCompanySignup(payload);
      setSubmittedEmail(email);
    } catch (error) {
      setSubmitError(
        error instanceof ApiError
          ? error.message
          : 'Could not submit your request. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <form className="qh-employer-form" onSubmit={handleSubmit} noValidate>
        <div className="qh-form-row">
          <div className="qh-form-field">
            <label htmlFor="employer-name">Your name</label>
            <input
              id="employer-name"
              name="name"
              type="text"
              required
              value={form.name}
              onChange={(event) => updateField('name', event.target.value)}
              onBlur={(event) => validateField('name', event.target.value)}
              placeholder="Your name"
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'employer-name-error' : undefined}
            />
            {errors.name && (
              <p id="employer-name-error" className="qh-employer-field-error">
                {errors.name}
              </p>
            )}
          </div>

          <div className="qh-form-field">
            <label htmlFor="employer-email">Work email</label>
            <input
              id="employer-email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
              onBlur={(event) => validateField('email', event.target.value)}
              placeholder="you@company.com"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'employer-email-error' : undefined}
            />
            {errors.email && (
              <p id="employer-email-error" className="qh-employer-field-error">
                {errors.email}
              </p>
            )}
          </div>
        </div>

        <div className="qh-form-row">
          <div className="qh-form-field">
            <label htmlFor="employer-company">Company name</label>
            <input
              id="employer-company"
              name="company"
              type="text"
              required
              value={form.company}
              onChange={(event) => updateField('company', event.target.value)}
              onBlur={() => {
                setErrors((current) => {
                  const next = { ...current };
                  if (form.company.trim()) delete next.company;
                  else next.company = 'Company name is required';
                  return next;
                });
              }}
              placeholder="Company name"
              aria-invalid={Boolean(errors.company)}
              aria-describedby={errors.company ? 'employer-company-error' : undefined}
            />
            {errors.company && (
              <p id="employer-company-error" className="qh-employer-field-error">
                {errors.company}
              </p>
            )}
          </div>

          <div className="qh-form-field">
            <label htmlFor="employer-role">Role you&apos;re hiring for</label>
            <input
              id="employer-role"
              name="role"
              type="text"
              value={form.role}
              onChange={(event) => updateField('role', event.target.value)}
              placeholder="e.g. Senior Engineer"
            />
          </div>
        </div>

        <div className="qh-form-row">
          <div className="qh-form-field">
            <label htmlFor="employer-location">Location</label>
            <select
              id="employer-location"
              className="qh-employer-location-select"
              style={{ color: '#f5f6f2' }}
              name="location"
              required
              value={form.location}
              onChange={(event) => updateField('location', event.target.value)}
              aria-invalid={Boolean(errors.location)}
              aria-describedby={errors.location ? 'employer-location-error' : undefined}
            >
              <option value="" disabled>
                Select location
              </option>
              {REGIONS.map((regionOption) => (
                <option
                  key={regionOption.slug}
                  value={regionOption.slug}
                  style={{ backgroundColor: '#f5f6f2', color: '#14150f' }}
                >
                  {regionOption.name}
                </option>
              ))}
            </select>
            {errors.location && (
              <p id="employer-location-error" className="qh-employer-field-error">
                {errors.location}
              </p>
            )}
          </div>

          <div className="qh-form-field">
            <label htmlFor="employer-timeline">Timeline</label>
            <select
              id="employer-timeline"
              name="timeline"
              value={form.timeline}
              onChange={(event) => updateField('timeline', event.target.value)}
            >
              <option value="" disabled>
                Select timeline
              </option>
              <option value="immediately">Immediately</option>
              <option value="30-days">Within 30 days</option>
              <option value="60-days">Within 60 days</option>
              <option value="flexible">Flexible</option>
            </select>
          </div>
        </div>

        <div className="qh-form-field">
          <label htmlFor="employer-notes">Anything else your recruiter should know</label>
          <textarea
            id="employer-notes"
            name="notes"
            value={form.notes}
            onChange={(event) => updateField('notes', event.target.value)}
            placeholder="Tell us anything useful about the role..."
          />
        </div>

        {submitError && (
          <p className="qh-employer-submit-error" role="alert">
            {submitError}
          </p>
        )}

        <button type="submit" className="qh-form-submit" disabled={submitting}>
          {submitting ? (
            <span className="qh-employer-submit-status">
              <LoaderCircle aria-hidden="true" /> Sending request…
            </span>
          ) : (
            'Send this role to our desk →'
          )}
        </button>

        <p className="qh-form-note">A recruiter replies within one business day.</p>
      </form>

      {submittedEmail && (
        <div className="qh-employer-success" role="presentation">
          <button
            type="button"
            className="qh-employer-success-backdrop"
            onClick={() => setSubmittedEmail(null)}
            aria-label="Close success message"
          />
          <section
            className="qh-employer-success-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="employer-success-title"
            aria-describedby="employer-success-description"
          >
            <button
              type="button"
              className="qh-employer-success-close"
              onClick={() => setSubmittedEmail(null)}
              aria-label="Close"
            >
              <X aria-hidden="true" />
            </button>
            <div className="qh-employer-success-icon" aria-hidden="true">
              <Check />
            </div>
            <h2 id="employer-success-title">Thank you</h2>
            <p id="employer-success-description">
              We&apos;ll review your request and get back to you at{' '}
              <strong>{submittedEmail}</strong> within 1-2 business days.
            </p>
            <button
              type="button"
              className="qh-employer-success-action"
              onClick={() => setSubmittedEmail(null)}
              autoFocus
            >
              GOT IT, THANKS
            </button>
          </section>
        </div>
      )}
    </>
  );
};
